/**
 * 门户站点共享数据
 * 所有门户子页面从此文件获取产品、方案、招聘等数据
 */

// ===================== 类型定义 =====================

export interface ProductItem {
  id: string
  name: string
  icon: string
  color: string
  bgColor: string
  desc: string
  descShort: string
  category: string
  features?: string[]
  price?: string
  industries?: string[]
}

export interface SolutionItem {
  id: string
  name: string
  icon: string
  color: string
  gradient: string
  desc: string
  descLong: string
  products: string[]
  cases: number
  effect?: string
}

export interface SolutionFeature {
  title: string
  desc: string
  icon: string
  iconColor: string
  iconBg: string
  image: string
  tags: { icon: string; text: string }[]
}

export interface SolutionDetail {
  heroGradient: string
  heroImage: string
  stats: { value: string; label: string }[]
  features: SolutionFeature[]
  customerCases: { name: string; industry: string; result: string }[]
  scenarios?: { image: string; title: string; desc: string }[]
}

export interface PartnerTier {
  level: string
  name: string
  price: string
  discount: string
  features: string[]
  borderColor: string
  highlighted?: boolean
  hot?: boolean
  badgeText?: string
}

export interface JobItem {
  id: string
  title: string
  department: string
  deptColor: string
  location: string
  experience: string
  education: string
  salary: string
  desc: string
  requirements: string[]
}

export interface NewsItem {
  id: string
  date: string
  day: string
  month: string
  title: string
  summary: string
  category: string
  content: string
}

export interface EventItem {
  id: string
  title: string
  desc: string
  date: string
  location: string
  status: string
  statusColor: string
  type: string
  gradient: string
  icon: string
  image?: string
  industries?: string[]
}

// ===================== 产品数据 =====================

export const products: ProductItem[] = [
  {
    id: 'website',
    name: '官网建设',
    icon: 'lucide:globe',
    color: '#0066FF',
    bgColor: '#EBF5FF',
    desc: '拖拽式可视化建站，300+行业模板，PC/H5/小程序三端同步，SEO友好结构，3分钟极速上线',
    descShort: '拖拽式建站，300+行业模板',
    category: '建站营销',
    features: [
      '300+行业模板',
      '可视化拖拽编辑',
      'PC/H5/小程序三端同步',
      'SEO友好结构',
      '自定义域名绑定',
      'CDN全球加速'
    ],
    price: '¥2999/年起',
    industries: ['零售电商', '餐饮服务', '房产建筑', '旅游出行']
  },
  {
    id: 'foreign-trade',
    name: '外贸官网',
    icon: 'lucide:ship',
    color: '#0891B2',
    bgColor: '#ECFEFF',
    desc: '多语言自动切换，8种语言覆盖全球，海外CDN加速，集成PayPal/Stripe等国际支付',
    descShort: '多语言建站，海外CDN',
    category: '建站营销',
    features: [
      '8种语言自动切换',
      '海外CDN 200+节点',
      'PayPal/Stripe支付',
      'Google SEO优化',
      '多币种结算',
      'WhatsApp在线客服'
    ],
    price: '¥5999/年起',
    industries: ['零售电商', '智能制造', '文化传媒']
  },
  {
    id: 'seo',
    name: 'SEO优化',
    icon: 'lucide:trending-up',
    color: '#00B96B',
    bgColor: '#ECFDF5',
    desc: '关键词排名追踪，AI内容生成，自动外链建设，搜索引擎友好的页面结构优化',
    descShort: '关键词排名追踪，AI内容',
    category: '建站营销',
    features: [
      '关键词排名追踪',
      'AI内容自动生成',
      '自动外链建设',
      '站长工具集成',
      '竞品分析',
      '排名实时监控'
    ],
    price: '¥1999/年起',
    industries: ['零售电商', '医疗健康', '文化传媒', '旅游出行']
  },
  {
    id: 'geo',
    name: 'GEO优化',
    icon: 'lucide:sparkles',
    color: '#7C3AED',
    bgColor: '#F3E8FF',
    desc: 'AI搜索引擎优化，结构化数据标记，语义化内容策略，适配ChatGPT/Perplexity等AI搜索',
    descShort: 'AI搜索优化，结构化标记',
    category: '建站营销',
    features: [
      'AI搜索引擎适配',
      '结构化数据标记',
      '语义化内容策略',
      'ChatGPT/Perplexity优化',
      '知识图谱构建',
      'AI回答溯源'
    ],
    price: '¥3999/年起',
    industries: ['零售电商', '文化传媒', '金融保险']
  },
  {
    id: 'shop',
    name: '商城系统',
    icon: 'lucide:shopping-cart',
    color: '#E11D48',
    bgColor: '#FFF1F2',
    desc: '多端商城，支持B2C/B2B/B2B2C/O2O模式，商品管理、订单流程、支付物流、营销工具全覆盖',
    descShort: '多端商城，商品支付物流',
    category: '电商交易',
    features: [
      'B2C/B2B/B2B2C/O2O多模式',
      '商品SPU/SKU管理',
      '订单全流程',
      '微信/支付宝支付',
      '物流跟踪',
      '优惠券/拼团/秒杀'
    ],
    price: '¥8999/年起',
    industries: ['零售电商', '餐饮服务', '医疗健康']
  },
  {
    id: 'member',
    name: '会员系统',
    icon: 'lucide:credit-card',
    color: '#FF9500',
    bgColor: '#FFFBEB',
    desc: '多级会员等级、积分体系、专属权益、会员卡发行，完整的会员运营工具箱',
    descShort: '等级积分权益，会员卡',
    category: '用户运营',
    features: [
      '多级会员等级',
      '积分体系',
      '会员卡发行',
      '专属权益管理',
      '生日特权',
      '等级升级提醒'
    ],
    price: '¥3999/年起',
    industries: ['零售电商', '餐饮服务', '医疗健康', '教育培训']
  },
  {
    id: 'service',
    name: '服务系统',
    icon: 'lucide:wrench',
    color: '#0891B2',
    bgColor: '#ECFEFF',
    desc: '预约派单、上门服务、技师管理、服务评价，适用于家政、维修、美业等上门服务场景',
    descShort: '预约派单上门服务',
    category: '电商交易',
    features: ['预约派单', '技师管理', '上门服务跟踪', '服务评价', '智能排班', '服务区域管理'],
    price: '¥4999/年起',
    industries: ['餐饮服务', '医疗健康', '房产建筑', '物流供应链']
  },
  {
    id: 'course',
    name: '培训课程',
    icon: 'lucide:graduation-cap',
    color: '#7C3AED',
    bgColor: '#F3E8FF',
    desc: '直播录播、线下课程、考试管理、证书发放，覆盖职业培训、企业内训、知识付费全场景',
    descShort: '直播录播线下，考试证书',
    category: '内容社区',
    features: ['直播/录播/线下', '考试管理', '证书发放', '课程评价', '学习进度追踪', '分销推广'],
    price: '¥5999/年起',
    industries: ['教育培训', '政务公共']
  },
  {
    id: 'ai-social',
    name: 'AI商务社交',
    icon: 'lucide:users',
    color: '#0066FF',
    bgColor: '#EBF5FF',
    desc: 'AI智能匹配，电子名片交换，商机广场，在线洽谈，助力商务人脉精准对接与高效转化',
    descShort: 'AI智能匹配，电子名片',
    category: 'AI智能',
    features: ['AI智能匹配', '电子名片', '商机广场', '在线洽谈', 'AI沟通助手', '人脉管理'],
    price: '¥5999/年起',
    industries: ['金融保险', '文化传媒', '科技互联网']
  },
  {
    id: 'im',
    name: 'IM即时通讯',
    icon: 'lucide:message-square',
    color: '#00B96B',
    bgColor: '#ECFDF5',
    desc: '即时消息群聊、音视频通话、文件传输、消息加密，企业级安全即时通讯解决方案',
    descShort: '即时消息群聊，音视频通话',
    category: 'AI智能',
    features: ['即时消息', '群组聊天', '文件传输', '音视频通话', '消息加密', '开放API'],
    price: '¥4999/年起',
    industries: ['科技互联网', '政务公共', '物流供应链']
  },
  {
    id: 'event',
    name: '活动报名',
    icon: 'lucide:calendar',
    color: '#FF9500',
    bgColor: '#FFFBEB',
    desc: '活动创建管理、在线报名、门票签到、活动营销推广，全流程活动运营管理平台',
    descShort: '活动管理报名，门票签到',
    category: '内容社区',
    features: ['活动管理', '报名表单', '门票管理', '签到系统', '活动营销', '数据统计'],
    price: '¥2999/年起',
    industries: ['教育培训', '文化传媒', '旅游出行']
  },
  {
    id: 'cms',
    name: '内容管理',
    icon: 'lucide:file-text',
    color: '#475569',
    bgColor: '#F1F5F9',
    desc: '文章栏目管理、富文本编辑器、媒体库、内容审核、多站点发布，专业CMS内容管理',
    descShort: '文章栏目富文本，媒体库',
    category: '内容社区',
    features: ['文章管理', '栏目管理', '富文本编辑', '媒体库', '内容审核', '多站点'],
    price: '¥1999/年起',
    industries: ['文化传媒', '政务公共', '科技互联网']
  },
  {
    id: 'moments',
    name: '动态朋友圈',
    icon: 'lucide:image',
    color: '#E11D48',
    bgColor: '#FFF1F2',
    desc: '图文视频动态发布、互动评论、好友关系、动态流，构建社区互动与私域社交场景',
    descShort: '图文视频动态，互动评论',
    category: '内容社区',
    features: ['动态发布', '图文视频', '互动功能', '好友关系', '动态流', '隐私设置'],
    price: '¥1999/年起',
    industries: ['零售电商', '科技互联网', '文化传媒']
  },
  {
    id: 'ai-match',
    name: 'AI婚恋社交',
    icon: 'lucide:heart',
    color: '#E11D48',
    bgColor: '#FFF1F2',
    desc: 'AI智能匹配、性格测试、实名认证、视频相亲，打造严肃婚恋社交平台',
    descShort: 'AI智能匹配，性格测试',
    category: 'AI智能',
    features: ['AI智能匹配', '性格测试', '实名认证', '视频相亲', 'AI红娘', '隐私保护'],
    price: '¥4999/年起',
    industries: ['科技互联网', '文化传媒']
  },
  {
    id: 'tournament',
    name: '赛事管理',
    icon: 'lucide:trophy',
    color: '#FF9500',
    bgColor: '#FFFBEB',
    desc: '赛程编排、在线报名、成绩排名、直播对接，全流程赛事运营管理数字化',
    descShort: '赛程编排报名，成绩排名',
    category: '内容社区',
    features: ['赛事管理', '赛程编排', '报名系统', '成绩管理', '排名系统', '直播对接'],
    price: '¥3999/年起',
    industries: ['文化传媒', '教育培训', '旅游出行']
  },
  {
    id: 'survey',
    name: '问卷调查',
    icon: 'lucide:clipboard-list',
    color: '#0066FF',
    bgColor: '#EBF5FF',
    desc: '问卷设计、逻辑跳转、数据收集、主题定制、分析看板导出，专业在线调研工具',
    descShort: '问卷设计逻辑跳转，分析',
    category: '内容社区',
    features: ['问卷设计', '题型管理', '逻辑跳转', '主题定制', '数据收集', '分析看板'],
    price: '¥999/年起',
    industries: ['政务公共', '教育培训', '医疗健康']
  },
  {
    id: 'h5',
    name: '微场景H5',
    icon: 'lucide:smartphone',
    color: '#7C3AED',
    bgColor: '#F3E8FF',
    desc: '拖拽式H5编辑器，丰富模板库与动画效果，快速制作营销宣传H5页面',
    descShort: '拖拽H5编辑，模板库动画',
    category: '建站营销',
    features: ['拖拽编辑', '模板库', '动画效果', '音乐配置', '表单收集', '作品管理'],
    price: '¥999/年起',
    industries: ['零售电商', '餐饮服务', '文化传媒', '旅游出行']
  }
]

// ===================== 解决方案列表 =====================

export const solutions: SolutionItem[] = [
  {
    id: 'retail',
    name: '零售电商解决方案',
    icon: 'lucide:shopping-bag',
    color: '#E11D48',
    gradient: 'linear-gradient(135deg,#E11D48,#7C3AED)',
    desc: '全渠道商城+会员运营+营销工具+数据中台，一站式零售数字化转型',
    descLong:
      '商城系统 + 会员系统 + 营销工具 + 数据中台，一站式零售数字化转型方案，打通线上线下全渠道',
    products: ['商城系统', '会员系统', '优惠券', '数据中台'],
    cases: 3,
    effect: '平均订单量+45%'
  },
  {
    id: 'manufacturing',
    name: '智能制造解决方案',
    icon: 'lucide:factory',
    color: '#7C3AED',
    gradient: 'linear-gradient(135deg,#7C3AED,#3B82F6)',
    desc: 'MES+ERP+SRM+数据中台，打造透明车间与智能供应链',
    descLong: 'MES + ERP + SRM + 数据中台，打造透明车间与智能供应链，实现生产全流程数字化管控',
    products: ['MES', 'ERP', 'SRM', '数据中台'],
    cases: 2,
    effect: '产能提升+30%'
  },
  {
    id: 'education',
    name: '教育培训解决方案',
    icon: 'lucide:book-open',
    color: '#0066FF',
    gradient: 'linear-gradient(135deg,#0066FF,#00B96B)',
    desc: '培训课程+会员+活动报名+CRM，教培机构全链路数字化运营',
    descLong: '培训课程 + 会员系统 + 活动报名 + CRM，打造线上线下融合的教育培训数字化平台',
    products: ['培训课程', '会员系统', '活动报名', 'CRM'],
    cases: 2,
    effect: '完课率+60%'
  },
  {
    id: 'healthcare',
    name: '医疗健康解决方案',
    icon: 'lucide:heart-pulse',
    color: '#10B981',
    gradient: 'linear-gradient(135deg,#10B981,#0891B2)',
    desc: '在线问诊+患者管理+预约挂号+电子病历，智慧医疗全场景覆盖',
    descLong: '在线问诊 + 患者管理 + 预约挂号 + 电子病历，构建互联网+医疗健康服务生态',
    products: ['会员系统', 'IM即时通讯', '预约系统', '数据中台'],
    cases: 2,
    effect: '患者满意度+40%'
  },
  {
    id: 'finance',
    name: '金融保险解决方案',
    icon: 'lucide:landmark',
    color: '#F59E0B',
    gradient: 'linear-gradient(135deg,#F59E0B,#E11D48)',
    desc: '智能风控+客户管理+产品配置+数据分析，金融保险数字化升级',
    descLong: '智能风控 + 客户管理 + 产品配置 + 数据分析，为金融机构提供安全可靠的数字化解决方案',
    products: ['CRM', 'OA审批', '数据中台', '自定义表单'],
    cases: 2,
    effect: '理赔效率+55%'
  },
  {
    id: 'catering',
    name: '餐饮旅游解决方案',
    icon: 'lucide:utensils',
    color: '#FF9500',
    gradient: 'linear-gradient(135deg,#FF9500,#E11D48)',
    desc: '自营外卖+会员营销+门店管理+供应链，降低平台佣金依赖',
    descLong: '自营外卖 + 会员营销 + 门店管理 + 供应链，帮助餐饮旅游企业构建私域流量池',
    products: ['商城系统', '会员系统', '预约系统', 'SCRM'],
    cases: 3,
    effect: '外卖订单+80%'
  },
  {
    id: 'realestate',
    name: '房地产解决方案',
    icon: 'lucide:building-2',
    color: '#8B5CF6',
    gradient: 'linear-gradient(135deg,#8B5CF6,#3B82F6)',
    desc: '楼盘展示+客户管理+在线看房+营销获客，地产营销全链路',
    descLong: '楼盘展示 + 客户管理 + 在线看房 + 营销获客，打造沉浸式数字化营销体验',
    products: ['官网建设', 'CRM', 'IM即时通讯', 'SEO优化'],
    cases: 2,
    effect: '线索转化+35%'
  },
  {
    id: 'government',
    name: '政府公共解决方案',
    icon: 'lucide:building',
    color: '#0891B2',
    gradient: 'linear-gradient(135deg,#0891B2,#0066FF)',
    desc: '政务公开+在线办事+数据治理+协同办公，数字政府一体化平台',
    descLong: '政务公开 + 在线办事 + 数据治理 + 协同办公，助力数字政府建设，提升政务服务效率',
    products: ['OA审批', '自定义表单', '数据中台', '官网建设'],
    cases: 2,
    effect: '办事效率+70%'
  },
  {
    id: 'media',
    name: '文化传媒解决方案',
    icon: 'lucide:film',
    color: '#EC4899',
    gradient: 'linear-gradient(135deg,#EC4899,#7C3AED)',
    desc: '内容管理+会员付费+活动运营+数据分析，内容行业全场景',
    descLong: '内容管理 + 会员付费 + 活动运营 + 数据分析，为文化传媒企业提供全链路数字化运营平台',
    products: ['CMS内容管理', '会员系统', '活动报名', '数据中台'],
    cases: 2,
    effect: '订阅转化+50%'
  },
  {
    id: 'agriculture',
    name: '农业解决方案',
    icon: 'lucide:sprout',
    color: '#84CC16',
    gradient: 'linear-gradient(135deg,#84CC16,#22C55E)',
    desc: '溯源管理+农资商城+专家服务+数据监测，智慧农业全链路',
    descLong: '溯源管理 + 农资商城 + 专家服务 + 数据监测，构建智慧农业全链路数字化平台',
    products: ['商城系统', '数据中台', '预约系统', 'IM即时通讯'],
    cases: 1,
    effect: '管理效率+60%'
  },
  {
    id: 'energy',
    name: '能源解决方案',
    icon: 'lucide:zap',
    color: '#EF4444',
    gradient: 'linear-gradient(135deg,#EF4444,#F97316)',
    desc: '设备监控+巡检管理+数据分析+智能预警，能源行业数字化',
    descLong: '设备监控 + 巡检管理 + 数据分析 + 智能预警，推动能源行业数字化转型',
    products: ['数据中台', 'OA审批', '自定义表单', 'IM即时通讯'],
    cases: 1,
    effect: '设备故障率-40%'
  }
]

// ===================== 解决方案详情 =====================

export const solutionDetails: Record<string, SolutionDetail> = {
  retail: {
    heroGradient: 'linear-gradient(135deg,#E11D48,#7C3AED)',
    heroImage: 'https://picsum.photos/seed/retail/1280/420',
    stats: [
      { value: '45%', label: '平均订单量提升' },
      { value: '28%', label: '客单价提升' },
      { value: '3+', label: '标杆客户案例' }
    ],
    features: [
      {
        title: '多端商城，全渠道触达',
        desc: 'PC官网、H5移动商城、微信小程序三端同步上架，商品库存实时同步、订单统一管理。消费者无论从哪个入口下单，后台统一处理发货与售后。',
        icon: 'lucide:shopping-cart',
        iconColor: '#E11D48',
        iconBg: '#FFF1F2',
        image: 'https://picsum.photos/seed/multistore/580/320',
        tags: [
          { icon: 'lucide:monitor', text: 'PC官网' },
          { icon: 'lucide:smartphone', text: 'H5商城' },
          { icon: 'lucide:scan', text: '小程序' }
        ]
      },
      {
        title: '会员体系，提升复购粘性',
        desc: '多级会员等级 + 积分体系 + 专属权益，让顾客从"偶尔买"变成"常回购"。自动积分发放、生日特权、等级升级提醒，让会员感受到被重视。',
        icon: 'lucide:credit-card',
        iconColor: '#FF9500',
        iconBg: '#FFFBEB',
        image: 'https://picsum.photos/seed/membership/580/320',
        tags: [
          { icon: 'lucide:award', text: '等级权益' },
          { icon: 'lucide:star', text: '积分商城' },
          { icon: 'lucide:gift', text: '生日特权' }
        ]
      },
      {
        title: '营销工具，引爆流量转化',
        desc: '优惠券、拼团、秒杀、直播带货四大营销引擎，一键创建活动、自动裂变传播。从拉新到促活到转化，每一步都有对应工具，让营销不再是"拍脑袋"。',
        icon: 'lucide:tag',
        iconColor: '#00B96B',
        iconBg: '#ECFDF5',
        image: 'https://picsum.photos/seed/marketing/580/320',
        tags: [
          { icon: 'lucide:ticket', text: '优惠券' },
          { icon: 'lucide:users', text: '拼团' },
          { icon: 'lucide:zap', text: '秒杀' },
          { icon: 'lucide:video', text: '直播带货' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某零售集团',
        industry: '零售电商',
        result:
          '部署商城系统+会员系统+数据中台，打通线上线下全渠道，订单量+45%，客单价+28%，复购率+35%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/retail1/400/200',
        title: '连锁门店统一管理',
        desc: '多家门店库存、订单、会员数据统一管控，总部实时监控各门店经营状况，一键调拨库存'
      },
      {
        image: 'https://picsum.photos/seed/retail2/400/200',
        title: '社交裂变电商',
        desc: '拼团+分销+优惠券组合裂变，老客拉新客，微信社群自动转化，低成本获客新模式'
      },
      {
        image: 'https://picsum.photos/seed/retail3/400/200',
        title: '直播带货+私域运营',
        desc: '直播间直接下单，粉丝自动沉淀为会员，直播后持续推送专属优惠，让流量不流失'
      }
    ]
  },
  manufacturing: {
    heroGradient: 'linear-gradient(135deg,#7C3AED,#001B5E)',
    heroImage: 'https://picsum.photos/seed/factory/1280/420',
    stats: [
      { value: '30%', label: '产能提升' },
      { value: '22%', label: '良率提升' },
      { value: '18%', label: '成本降低' }
    ],
    features: [
      {
        title: 'MES制造执行，车间透明化',
        desc: '从排产到报工到质检，车间每一个环节实时记录、实时看板。工单进度、设备状态、良率曲线一目了然，异常自动预警推送。',
        icon: 'lucide:cpu',
        iconColor: '#7C3AED',
        iconBg: '#F3E8FF',
        image: 'https://picsum.photos/seed/mes-system/580/320',
        tags: [
          { icon: 'lucide:clipboard-list', text: '工单管理' },
          { icon: 'lucide:gauge', text: '实时看板' },
          { icon: 'lucide:shield-check', text: '质检追溯' }
        ]
      },
      {
        title: 'ERP企业资源计划',
        desc: '采购、库存、销售、财务一体化管理，多工厂多组织架构支持，实时财务报表，智能MRP物料需求计划。',
        icon: 'lucide:briefcase',
        iconColor: '#0891B2',
        iconBg: '#ECFEFF',
        image: 'https://picsum.photos/seed/erp/580/320',
        tags: [
          { icon: 'lucide:package', text: '采购管理' },
          { icon: 'lucide:boxes', text: '库存管理' },
          { icon: 'lucide:file-text', text: '财务报表' }
        ]
      },
      {
        title: 'SRM供应商管理',
        desc: '供应商准入、评估、分级管理，采购协同平台，在线询比价，合同电子化，供应商绩效自动评估。',
        icon: 'lucide:truck',
        iconColor: '#0066FF',
        iconBg: '#EBF5FF',
        image: 'https://picsum.photos/seed/srm/580/320',
        tags: [
          { icon: 'lucide:user-check', text: '准入评估' },
          { icon: 'lucide:scale', text: '询比价' },
          { icon: 'lucide:award', text: '绩效评估' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某制造企业',
        industry: '智能制造',
        result: '部署MES+ERP+SRM，实现生产全流程数字化和质量追溯，产能+30%，良率+22%，成本-18%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/mfg1/400/200',
        title: '离散制造车间管控',
        desc: '多工序多品种车间，MES实时追踪每道工序进度，自动排产优化，杜绝窝工和物料浪费'
      },
      {
        image: 'https://picsum.photos/seed/mfg2/400/200',
        title: '全链路质量追溯',
        desc: '从原材料批次到成品出库，扫码即可追溯全链路，质量问题一键定位根因，快速召回'
      },
      {
        image: 'https://picsum.photos/seed/mfg3/400/200',
        title: 'AI智能排产',
        desc: '基于订单交期、设备产能、物料库存，AI自动生成最优排产方案，交期达成率提升35%'
      }
    ]
  },
  education: {
    heroGradient: 'linear-gradient(135deg,#0066FF,#00B96B)',
    heroImage: 'https://picsum.photos/seed/education/1280/420',
    stats: [
      { value: '50%', label: '学员增长' },
      { value: '60%', label: '完课率提升' },
      { value: '40%', label: '续费率提升' }
    ],
    features: [
      {
        title: '直播+录播双模式教学',
        desc: '高清直播课堂，互动白板，举手连麦。录播课程自动转码，支持倍速播放、章节笔记、离线下载。',
        icon: 'lucide:play-circle',
        iconColor: '#0066FF',
        iconBg: '#EBF5FF',
        image: 'https://picsum.photos/seed/edutech/580/320',
        tags: [
          { icon: 'lucide:video', text: '高清直播' },
          { icon: 'lucide:edit', text: '互动白板' },
          { icon: 'lucide:download', text: '离线下载' }
        ]
      },
      {
        title: '考试认证体系',
        desc: '题库管理、随机组卷、防作弊监控、自动阅卷。支持电子证书发放与验证，打通学-练-考-证全链路。',
        icon: 'lucide:clipboard-check',
        iconColor: '#00B96B',
        iconBg: '#ECFDF5',
        image: 'https://picsum.photos/seed/exam/580/320',
        tags: [
          { icon: 'lucide:archive', text: '题库管理' },
          { icon: 'lucide:shuffle', text: '随机组卷' },
          { icon: 'lucide:award', text: '电子证书' }
        ]
      },
      {
        title: 'CRM招生管理',
        desc: '线索来源追踪、意向度评分、跟进提醒、转化漏斗分析。让每个潜在学员都不流失。',
        icon: 'lucide:user-plus',
        iconColor: '#FF9500',
        iconBg: '#FFFBEB',
        image: 'https://picsum.photos/seed/crm-edu/580/320',
        tags: [
          { icon: 'lucide:filter', text: '意向评分' },
          { icon: 'lucide:bell', text: '跟进提醒' },
          { icon: 'lucide:trending-up', text: '转化分析' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某教育机构',
        industry: '教育培训',
        result:
          '部署培训课程+会员系统+CRM，打造线上线下融合教学，学员增长+50%，完课率+60%，续费率+40%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/edu1/400/200',
        title: 'K12教培机构',
        desc: '课后辅导、寒暑假班、赛事培训，排课签到续费一体化运营，家长端实时查看进度'
      },
      {
        image: 'https://picsum.photos/seed/edu2/400/200',
        title: '职业技能培训',
        desc: '线上直播+线下实操混合教学，证书发放、课时管理、企业团购一站式搞定'
      },
      {
        image: 'https://picsum.photos/seed/edu3/400/200',
        title: '企业内训平台',
        desc: '新员工入职培训、技能认证、学习进度追踪，HR一键生成培训报表，合规留痕'
      }
    ]
  },
  healthcare: {
    heroGradient: 'linear-gradient(135deg,#10B981,#0891B2)',
    heroImage: 'https://picsum.photos/seed/healthcare/1280/420',
    stats: [
      { value: '80%', label: '问诊效率提升' },
      { value: '15min', label: '平均候诊缩短' },
      { value: '95%', label: '患者满意度' }
    ],
    features: [
      {
        title: '在线问诊平台',
        desc: '图文问诊、视频通话、电子处方，支持多科室排班。医生在线状态实时展示，智能导诊推荐。',
        icon: 'lucide:video',
        iconColor: '#10B981',
        iconBg: '#ECFDF5',
        image: 'https://picsum.photos/seed/telemed/580/320',
        tags: [
          { icon: 'lucide:message-square', text: '图文问诊' },
          { icon: 'lucide:video', text: '视频问诊' },
          { icon: 'lucide:file-text', text: '电子处方' }
        ]
      },
      {
        title: '患者健康档案',
        desc: '电子病历、检验报告、用药记录全生命周期管理。患者扫码即可查看完整就医历史。',
        icon: 'lucide:folder-open',
        iconColor: '#0891B2',
        iconBg: '#ECFEFF',
        image: 'https://picsum.photos/seed/health-record/580/320',
        tags: [
          { icon: 'lucide:file-medical', text: '电子病历' },
          { icon: 'lucide:flask-conical', text: '检验报告' },
          { icon: 'lucide:pill', text: '用药记录' }
        ]
      },
      {
        title: '预约挂号系统',
        desc: '科室排班、号源管理、智能分诊、到院签到。支持微信小程序、APP多渠道预约。',
        icon: 'lucide:calendar',
        iconColor: '#7C3AED',
        iconBg: '#F3E8FF',
        image: 'https://picsum.photos/seed/appointment/580/320',
        tags: [
          { icon: 'lucide:calendar-plus', text: '在线预约' },
          { icon: 'lucide:stethoscope', text: '智能分诊' },
          { icon: 'lucide:qrcode', text: '到院签到' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某医疗集团',
        industry: '医疗健康',
        result:
          '部署会员系统+服务系统+IM即时通讯，建立患者全周期管理平台，患者满意度+40%，预约效率+60%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/healthcare-s1/400/200',
        title: '连锁诊所统一管理',
        desc: '多家诊所患者档案、排班、库存统一管控，总部实时查看各诊所经营数据，一键调拨药品耗材'
      },
      {
        image: 'https://picsum.photos/seed/healthcare-s2/400/200',
        title: '慢病管理平台',
        desc: '糖尿病、高血压等慢病患者定期随访提醒、用药管理、指标监测，医生远程调整方案，降低住院率'
      },
      {
        image: 'https://picsum.photos/seed/healthcare-s3/400/200',
        title: '体检中心数字化',
        desc: '在线预约体检套餐、报告自动生成推送、异常指标智能标注，检后健康管理持续跟踪'
      }
    ]
  },
  finance: {
    heroGradient: 'linear-gradient(135deg,#F59E0B,#E11D48)',
    heroImage: 'https://picsum.photos/seed/finance/1280/420',
    stats: [
      { value: '99.9%', label: '风控准确率' },
      { value: '40%', label: '运营成本降低' },
      { value: '5min', label: '审批速度' }
    ],
    features: [
      {
        title: '智能风控引擎',
        desc: '多维数据采集，AI风险评估模型，实时欺诈检测，自动审批策略引擎。',
        icon: 'lucide:shield',
        iconColor: '#F59E0B',
        iconBg: '#FFFBEB',
        image: 'https://picsum.photos/seed/risk/580/320',
        tags: [
          { icon: 'lucide:database', text: '多维数据' },
          { icon: 'lucide:cpu', text: 'AI风控' },
          { icon: 'lucide:zap', text: '实时检测' }
        ]
      },
      {
        title: '客户全生命周期管理',
        desc: '获客、活客、留客、转化全链路管理，客户画像精准建模，智能营销推荐。',
        icon: 'lucide:users',
        iconColor: '#0066FF',
        iconBg: '#EBF5FF',
        image: 'https://picsum.photos/seed/fin-crm/580/320',
        tags: [
          { icon: 'lucide:user-plus', text: '获客' },
          { icon: 'lucide:target', text: '精准画像' },
          { icon: 'lucide:repeat', text: '复购转化' }
        ]
      },
      {
        title: '数据中台分析',
        desc: '经营仪表盘、风险报表、监管报送，实时数据可视化，辅助经营决策。',
        icon: 'lucide:bar-chart-2',
        iconColor: '#7C3AED',
        iconBg: '#F3E8FF',
        image: 'https://picsum.photos/seed/fin-data/580/320',
        tags: [
          { icon: 'lucide:pie-chart', text: '经营仪表盘' },
          { icon: 'lucide:file-text', text: '监管报送' },
          { icon: 'lucide:trending-up', text: '趋势分析' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某消费金融公司',
        industry: '金融保险',
        result: '部署CRM+OA+数据中台，实现客户全生命周期管理，风控准确率99.9%，审批从2天缩短到5分钟'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/finance-s1/400/200',
        title: '保险代理人展业平台',
        desc: '在线出单、客户管理、续保提醒、佣金结算一站式平台，代理人随时随地展业，管理层实时看产能'
      },
      {
        image: 'https://picsum.photos/seed/finance-s2/400/200',
        title: '银行零售信贷数字化',
        desc: '线上进件、自动审批、电子签约、贷后监控全流程线上化，审批时效从3天缩短至30分钟'
      },
      {
        image: 'https://picsum.photos/seed/finance-s3/400/200',
        title: '证券投顾服务平台',
        desc: '投顾与客户在线沟通、产品推荐、组合跟踪、风险测评，合规留痕，投顾管理效率翻倍'
      }
    ]
  },
  catering: {
    heroGradient: 'linear-gradient(135deg,#FF9500,#E11D48)',
    heroImage: 'https://picsum.photos/seed/catering/1280/420',
    stats: [
      { value: '30%', label: '营销成本降低' },
      { value: '2x', label: '会员复购率' },
      { value: '50+', label: '门店覆盖' }
    ],
    features: [
      {
        title: '自营外卖平台',
        desc: '摆脱第三方平台抽佣，自营外卖小程序，骑手对接，订单实时跟踪。',
        icon: 'lucide:truck',
        iconColor: '#FF9500',
        iconBg: '#FFFBEB',
        image: 'https://picsum.photos/seed/takeout/580/320',
        tags: [
          { icon: 'lucide:smartphone', text: '小程序点餐' },
          { icon: 'lucide:bike', text: '骑手对接' },
          { icon: 'lucide:map-pin', text: '订单跟踪' }
        ]
      },
      {
        title: '会员储值营销',
        desc: '储值返现、消费积分、生日礼券、会员专享价，构建餐饮私域流量。',
        icon: 'lucide:gift',
        iconColor: '#E11D48',
        iconBg: '#FFF1F2',
        image: 'https://picsum.photos/seed/catering-member/580/320',
        tags: [
          { icon: 'lucide:wallet', text: '储值返现' },
          { icon: 'lucide:star', text: '消费积分' },
          { icon: 'lucide:cake', text: '生日礼券' }
        ]
      },
      {
        title: '多门店管理',
        desc: '统一菜单、分店库存、门店独立核算、区域报表，连锁化运营管理。',
        icon: 'lucide:store',
        iconColor: '#0891B2',
        iconBg: '#ECFEFF',
        image: 'https://picsum.photos/seed/chain-store/580/320',
        tags: [
          { icon: 'lucide:layers', text: '统一菜单' },
          { icon: 'lucide:map', text: '分店管理' },
          { icon: 'lucide:calculator', text: '独立核算' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某连锁餐饮',
        industry: '餐饮旅游',
        result: '部署商城系统+会员系统+营销工具，建立自营外卖和会员体系，外卖订单+80%，会员转化+45%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/catering-s1/400/200',
        title: '连锁餐饮品牌',
        desc: '多门店统一菜单、统一价格、统一会员，总部实时监控各店营收和评价，爆款菜品一键同步全门店'
      },
      {
        image: 'https://picsum.photos/seed/catering-s2/400/200',
        title: '酒店民宿预订平台',
        desc: '房态管理、在线预订、入住办理、点评管理一体化，支持多房型多价格策略，旺季自动调价'
      },
      {
        image: 'https://picsum.photos/seed/catering-s3/400/200',
        title: '景区票务+二次消费',
        desc: '电子门票+园内消费一码通，实时客流监控、排队时间预测，提升游客体验和二次消费转化'
      }
    ]
  },
  realestate: {
    heroGradient: 'linear-gradient(135deg,#8B5CF6,#3B82F6)',
    heroImage: 'https://picsum.photos/seed/realestate/1280/420',
    stats: [
      { value: '3x', label: '线索转化率' },
      { value: '70%', label: '看房效率提升' },
      { value: '500+', label: '楼盘项目' }
    ],
    features: [
      {
        title: 'VR在线看房',
        desc: '720°全景看房，户型图3D展示，样板间沉浸式体验，足不出户完成看房。',
        icon: 'lucide:box',
        iconColor: '#8B5CF6',
        iconBg: '#F3E8FF',
        image: 'https://picsum.photos/seed/vr-tour/580/320',
        tags: [
          { icon: 'lucide:eye', text: '720°全景' },
          { icon: 'lucide:layout', text: '3D户型图' },
          { icon: 'lucide:home', text: '样板间' }
        ]
      },
      {
        title: '客户意向管理',
        desc: '看房预约、意向跟进、成交转化，全流程客户管理。智能匹配客户与房源。',
        icon: 'lucide:users',
        iconColor: '#0066FF',
        iconBg: '#EBF5FF',
        image: 'https://picsum.photos/seed/realestate-crm/580/320',
        tags: [
          { icon: 'lucide:calendar', text: '看房预约' },
          { icon: 'lucide:heart', text: '意向跟进' },
          { icon: 'lucide:trending-up', text: '成交转化' }
        ]
      },
      {
        title: '楼盘展示官网',
        desc: '楼盘信息、户型鉴赏、周边配套、价格公示，一站式楼盘数字化展示平台。',
        icon: 'lucide:building',
        iconColor: '#0891B2',
        iconBg: '#ECFEFF',
        image: 'https://picsum.photos/seed/property-show/580/320',
        tags: [
          { icon: 'lucide:image', text: '楼盘展示' },
          { icon: 'lucide:map', text: '周边配套' },
          { icon: 'lucide:dollar-sign', text: '价格公示' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某知名地产商',
        industry: '房地产',
        result: '部署官网建设+CRM+SEO优化，打造品牌官网+电商一体化，线索转化+35%，在线转化+55%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/realestate-s1/400/200',
        title: '新房楼盘营销',
        desc: '单个楼盘从预热到开盘到顺销全周期数字化营销，在线认筹、选房、签约一站式，去化速度大幅提升'
      },
      {
        image: 'https://picsum.photos/seed/realestate-s2/400/200',
        title: '房产中介门店',
        desc: '房源共享协作、客户公海流转、行程量化管理，经纪人从入职到开单全流程赋能'
      },
      {
        image: 'https://picsum.photos/seed/realestate-s3/400/200',
        title: '物业社区服务',
        desc: '在线报修、物业缴费、社区公告、邻里互动一站式平台，提升业主满意度和物业费收缴率'
      }
    ]
  },
  government: {
    heroGradient: 'linear-gradient(135deg,#0891B2,#0066FF)',
    heroImage: 'https://picsum.photos/seed/government/1280/420',
    stats: [
      { value: '90%', label: '事项在线办理' },
      { value: '24h', label: '全天候服务' },
      { value: '50+', label: '部门协同' }
    ],
    features: [
      {
        title: '政务公开平台',
        desc: '政策法规、通知公告、财政预算、人事任免，政府信息全面公开透明。',
        icon: 'lucide:file-text',
        iconColor: '#0891B2',
        iconBg: '#ECFEFF',
        image: 'https://picsum.photos/seed/gov-open/580/320',
        tags: [
          { icon: 'lucide:scale', text: '政策法规' },
          { icon: 'lucide:bell', text: '通知公告' },
          { icon: 'lucide:wallet', text: '财政预算' }
        ]
      },
      {
        title: '一网通办',
        desc: '行政审批、证件办理、缴费纳税、社保查询，200+事项在线办理。',
        icon: 'lucide:check-square',
        iconColor: '#0066FF',
        iconBg: '#EBF5FF',
        image: 'https://picsum.photos/seed/gov-service/580/320',
        tags: [
          { icon: 'lucide:file-check', text: '行政审批' },
          { icon: 'lucide:credit-card', text: '在线缴费' },
          { icon: 'lucide:shield', text: '社保查询' }
        ]
      },
      {
        title: '数据治理中台',
        desc: '跨部门数据归集、数据质量监控、数据共享交换，辅助科学决策。',
        icon: 'lucide:database',
        iconColor: '#7C3AED',
        iconBg: '#F3E8FF',
        image: 'https://picsum.photos/seed/gov-data/580/320',
        tags: [
          { icon: 'lucide:layers', text: '数据归集' },
          { icon: 'lucide:refresh-cw', text: '共享交换' },
          { icon: 'lucide:bar-chart-2', text: '决策辅助' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某区政府',
        industry: '政府公共',
        result: '部署OA审批+自定义表单+数据中台，实现政务数字化，90%事项在线办理，办事效率+70%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/government-s1/400/200',
        title: '政务服务大厅',
        desc: '线上线下融合服务，群众在线预约、现场扫码取号、窗口叫号办理，办事进度短信实时推送'
      },
      {
        image: 'https://picsum.photos/seed/government-s2/400/200',
        title: '智慧城市大脑',
        desc: '城市运行态势实时大屏，交通、环保、应急、城管多维度数据可视化，辅助城市管理者决策'
      },
      {
        image: 'https://picsum.photos/seed/government-s3/400/200',
        title: '基层治理平台',
        desc: '网格化管理、事件上报、任务派发、结果反馈全闭环，打通基层治理最后一公里'
      }
    ]
  },
  media: {
    heroGradient: 'linear-gradient(135deg,#EC4899,#7C3AED)',
    heroImage: 'https://picsum.photos/seed/media/1280/420',
    stats: [
      { value: '5x', label: '内容产出效率' },
      { value: '60%', label: '会员付费转化' },
      { value: '100w+', label: '日活用户' }
    ],
    features: [
      {
        title: '内容管理平台',
        desc: '图文、视频、音频多内容形态管理，多频道运营，内容审核流程，版权保护。',
        icon: 'lucide:film',
        iconColor: '#EC4899',
        iconBg: '#FCE7F3',
        image: 'https://picsum.photos/seed/media-cms/580/320',
        tags: [
          { icon: 'lucide:image', text: '图文' },
          { icon: 'lucide:video', text: '视频' },
          { icon: 'lucide:headphones', text: '音频' }
        ]
      },
      {
        title: '会员付费体系',
        desc: '会员等级、付费内容、积分商城、打赏机制，多元化变现模式。',
        icon: 'lucide:crown',
        iconColor: '#7C3AED',
        iconBg: '#F3E8FF',
        image: 'https://picsum.photos/seed/media-vip/580/320',
        tags: [
          { icon: 'lucide:star', text: '会员等级' },
          { icon: 'lucide:lock', text: '付费内容' },
          { icon: 'lucide:coins', text: '打赏' }
        ]
      },
      {
        title: '活动运营',
        desc: '线上直播、粉丝见面会、投票评选、签到打卡，多维度粉丝运营工具。',
        icon: 'lucide:calendar',
        iconColor: '#FF9500',
        iconBg: '#FFFBEB',
        image: 'https://picsum.photos/seed/media-event/580/320',
        tags: [
          { icon: 'lucide:video', text: '线上直播' },
          { icon: 'lucide:users', text: '粉丝见面' },
          { icon: 'lucide:check-circle', text: '签到打卡' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某文化传媒公司',
        industry: '文化传媒',
        result:
          '部署CMS内容管理+会员系统+数据中台，内容产出效率提升5倍，会员付费转化率60%，订阅转化+50%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/media-s1/400/200',
        title: '新闻资讯平台',
        desc: '海量内容高效采编发，AI辅助写作+智能配图+自动标签分类，7×24小时不间断资讯更新'
      },
      {
        image: 'https://picsum.photos/seed/media-s2/400/200',
        title: '知识付费平台',
        desc: '专栏/课程/问答多种付费形态，创作者自主定价、实时分账，平台抽佣透明可查'
      },
      {
        image: 'https://picsum.photos/seed/media-s3/400/200',
        title: 'MCN机构',
        desc: '达人管理、内容排期、商单对接、数据复盘一体化，提升达人孵化效率和商业变现能力'
      }
    ]
  },
  agriculture: {
    heroGradient: 'linear-gradient(135deg,#84CC16,#22C55E)',
    heroImage: 'https://picsum.photos/seed/agriculture/1280/420',
    stats: [
      { value: '60%', label: '管理效率提升' },
      { value: '100%', label: '全程可追溯' },
      { value: '50%', label: '产能提升' }
    ],
    features: [
      {
        title: '农产品溯源管理',
        desc: '从种植到采收到加工到流通，全链路扫码追溯，消费者一扫即知农产品"前世今生"，增强购买信心。',
        icon: 'lucide:search',
        iconColor: '#84CC16',
        iconBg: '#ECFDF5',
        image: 'https://picsum.photos/seed/agri-trace/580/320',
        tags: [
          { icon: 'lucide:leaf', text: '种植记录' },
          { icon: 'lucide:qr-code', text: '扫码追溯' },
          { icon: 'lucide:truck', text: '流通追踪' }
        ]
      },
      {
        title: '农资商城',
        desc: '种子、肥料、农药、农机在线采购，厂家直供价格透明，农户一键下单配送到村，降低采购成本。',
        icon: 'lucide:shopping-cart',
        iconColor: '#22C55E',
        iconBg: '#DCFCE7',
        image: 'https://picsum.photos/seed/agri-shop/580/320',
        tags: [
          { icon: 'lucide:package', text: '农资采购' },
          { icon: 'lucide:bike', text: '配送到村' },
          { icon: 'lucide:shield', text: '厂家直供' }
        ]
      },
      {
        title: '专家服务+数据监测',
        desc: '在线农技咨询、病虫害识别AI诊断、气象土壤实时监测，用科技为农业生产保驾护航。',
        icon: 'lucide:thermometer',
        iconColor: '#F59E0B',
        iconBg: '#FFFBEB',
        image: 'https://picsum.photos/seed/agri-expert/580/320',
        tags: [
          { icon: 'lucide:message-square', text: '农技咨询' },
          { icon: 'lucide:cpu', text: 'AI诊断' },
          { icon: 'lucide:cloud-rain', text: '气象监测' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某农业合作社',
        industry: '农业',
        result: '部署商城系统+数据中台+IM即时通讯，建成智慧农业平台，管理效率+60%，全程可追溯100%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/agriculture-s1/400/200',
        title: '农产品溯源平台',
        desc: '从种植到餐桌全程扫码追溯，种植记录、施肥用药、检测报告全公开，消费者扫码即知农产品"前世今生"'
      },
      {
        image: 'https://picsum.photos/seed/agriculture-s2/400/200',
        title: '智慧农业示范园',
        desc: '物联网+大数据驱动的精准种植，水肥一体化自动控制，无人机巡检，示范园区产能提升50%'
      },
      {
        image: 'https://picsum.photos/seed/agriculture-s3/400/200',
        title: '农资连锁店',
        desc: '多门店库存共享、会员统一管理、农技服务预约，从卖产品升级为卖服务，提升门店竞争力'
      }
    ]
  },
  energy: {
    heroGradient: 'linear-gradient(135deg,#EF4444,#F97316)',
    heroImage: 'https://picsum.photos/seed/energy/1280/420',
    stats: [
      { value: '40%', label: '设备故障率降低' },
      { value: '24/7', label: '全天候监控' },
      { value: '99.5%', label: '供电可靠性' }
    ],
    features: [
      {
        title: '设备运行监控',
        desc: '发电机组、输电线路、变电站设备实时状态监控，异常自动告警，故障快速定位，减少非计划停机。',
        icon: 'lucide:activity',
        iconColor: '#EF4444',
        iconBg: '#FEE2E2',
        image: 'https://picsum.photos/seed/energy-monitor/580/320',
        tags: [
          { icon: 'lucide:bar-chart', text: '实时监控' },
          { icon: 'lucide:bell-ring', text: '自动告警' },
          { icon: 'lucide:map-pin', text: '故障定位' }
        ]
      },
      {
        title: '巡检与检修管理',
        desc: '巡检计划自动生成、App扫码执行、隐患拍照上报、检修工单全流程闭环，保障设备安全运行。',
        icon: 'lucide:clipboard-check',
        iconColor: '#F97316',
        iconBg: '#FFF7ED',
        image: 'https://picsum.photos/seed/energy-inspect/580/320',
        tags: [
          { icon: 'lucide:calendar', text: '巡检计划' },
          { icon: 'lucide:smartphone', text: 'App执行' },
          { icon: 'lucide:wrench', text: '检修工单' }
        ]
      },
      {
        title: '数据分析与预警',
        desc: '设备运行数据趋势分析、负荷预测、能效优化，AI模型自动预警潜在故障风险，防患于未然。',
        icon: 'lucide:trending-up',
        iconColor: '#0066FF',
        iconBg: '#EBF5FF',
        image: 'https://picsum.photos/seed/energy-data/580/320',
        tags: [
          { icon: 'lucide:line-chart', text: '趋势分析' },
          { icon: 'lucide:zap', text: '负荷预测' },
          { icon: 'lucide:cpu', text: 'AI预警' }
        ]
      }
    ],
    customerCases: [
      {
        name: '某能源集团',
        industry: '能源',
        result:
          '部署数据中台+OA审批+IM即时通讯，建成数字化运营平台，设备故障率-40%，供电可靠性99.5%'
      }
    ],
    scenarios: [
      {
        image: 'https://picsum.photos/seed/energy-s1/400/200',
        title: '发电企业数字化',
        desc: '机组运行监控、检修管理、技术监督、两票管理一体化，提升发电效率和安全管理水平'
      },
      {
        image: 'https://picsum.photos/seed/energy-s2/400/200',
        title: '电网运维管理',
        desc: '变电站巡检、线路巡视、抢修调度、停电管理全流程数字化，供电可靠性大幅提升'
      },
      {
        image: 'https://picsum.photos/seed/energy-s3/400/200',
        title: '新能源电站运营',
        desc: '光伏/风电电站远程监控、发电量预测、清洗维护排程，提升发电效率和运维效率'
      }
    ]
  }
}

// ===================== 合作伙伴代理档位 =====================

export const partnerTiers: PartnerTier[] = [
  {
    level: '一星',
    name: '基础代理',
    price: '预存 10,000 元',
    discount: '终身拿货 8 折',
    borderColor: '#D1D5DB',
    features: ['标准区县地域保护', '自主发展下级代理', '全套招商物料支持', '产品终身更新同步']
  },
  {
    level: '二星',
    name: '区域代理',
    price: '预存 50,000 元',
    discount: '终身拿货 7 折',
    borderColor: '#6B7280',
    features: [
      '扩大市级地域保护范围',
      '下级差价利润更高',
      '模块独立交付售卖权限',
      '专属运营一对一扶持'
    ]
  },
  {
    level: '三星',
    name: '市级独家代理',
    price: '预存 100,000 元',
    discount: '终身拿货 5 折',
    borderColor: '#0066FF',
    hot: true,
    badgeText: '热门',
    features: ['地级市全域独家保护', '全渠道招商权限开放', '高差价下级分润', '广告生态高额分润']
  },
  {
    level: '四星',
    name: '大区代理',
    price: '预存 200,000 元',
    discount: '终身拿货 3 折',
    borderColor: '#7C3AED',
    features: [
      '省会/片区独家保护',
      '下级差价收益大幅提升',
      '私有化源码交付资质',
      '平台优先资源扶持'
    ]
  },
  {
    level: '五星',
    name: '省级独家代理',
    price: '预存 500,000 元',
    discount: '终身拿货 1 折',
    borderColor: '#F59E0B',
    features: [
      '全省全域独家排他授权',
      '近乎成本拿货，利润最大化',
      '全模块私有化交付权限',
      '平台运营全渠道扶持'
    ]
  },
  {
    level: '战略总代',
    name: '（顶级）',
    price: '预存 1,000,000 元',
    discount: '所有产品终身免费拿',
    borderColor: '#FFB800',
    highlighted: true,
    badgeText: '推荐',
    features: [
      '整片省份终身独家运营权',
      '无任何拿货货款成本',
      '旗下所有下级资金全部自有',
      '平台最高优先级战略扶持'
    ]
  }
]

export const partnerBenefits = [
  {
    icon: 'lucide:package',
    title: '模块化独立架构',
    desc: '各模块独立部署、独立售卖交付，底层加密Jar包隔离',
    color: '#0066FF',
    bgColor: '#EBF5FF'
  },
  {
    icon: 'lucide:shield-check',
    title: '全部终身授权',
    desc: '拿货终身使用、终身售卖，无年费无续期成本',
    color: '#00B96B',
    bgColor: '#ECFDF5'
  },
  {
    icon: 'lucide:map-pin',
    title: '严格地域保护',
    desc: '区域独家授权，跨区抢客系统自动拦截',
    color: '#FF9500',
    bgColor: '#FFFBEB'
  },
  {
    icon: 'lucide:dollar-sign',
    title: '下级全额收益归你',
    desc: '下级预存款、消费全部归属上级，平台不抽成',
    color: '#7C3AED',
    bgColor: '#F3E8FF'
  },
  {
    icon: 'lucide:radio',
    title: '跨租户广告生态',
    desc: '租户商品互通投放，长期稳定广告分润收益',
    color: '#0891B2',
    bgColor: '#ECFEFF'
  }
]

// ===================== 企业信息数据 =====================

export const companyStats = [
  { value: '100', suffix: '+', label: '自研产品', color: '#0066FF' },
  { value: '5000', suffix: '+', label: '服务企业', color: '#00B96B' },
  { value: '15', suffix: '+', label: '年行业深耕', color: '#7C3AED' },
  { value: '99.9', suffix: '%', label: '系统可用性', color: '#FF9500' }
]

export const milestones = [
  {
    year: '2026',
    title: '产品矩阵全面覆盖',
    desc: '100+款自研产品覆盖建站营销、电商交易、AI智能、业务中台、数据中台全场景，服务企业突破500家',
    color: '#0066FF',
    glowColor: '#EBF5FF'
  },
  {
    year: '2024',
    title: 'AI智能产品线发布',
    desc: '推出AI商务社交、AI婚恋社交等智能产品，引入大模型技术赋能企业社交与精准匹配场景',
    color: '#00B96B',
    glowColor: '#ECFDF5'
  },
  {
    year: '2022',
    title: '业务中台全面升级',
    desc: '完成OA/CRM/ERP/MES/SRM五大业务中台产品矩阵搭建，服务制造、零售、服务等多个行业',
    color: '#7C3AED',
    glowColor: '#F3E8FF'
  },
  {
    year: '2020',
    title: '电商生态全面布局',
    desc: '上线商城系统、会员系统、服务系统，构建完整的电商交易闭环，服务企业突破200家',
    color: '#FF9500',
    glowColor: '#FFFBEB'
  },
  {
    year: '2018',
    title: '建站营销产品线成型',
    desc: '官网建设、外贸官网、SEO优化、GEO优化四大产品齐发，帮助企业快速构建线上品牌阵地',
    color: '#0891B2',
    glowColor: '#ECFEFF'
  },
  {
    year: '2015',
    title: '公司成立',
    desc: '深圳市紫金山数字智能有限公司正式注册成立，确立"以技术驱动企业数字化转型"的核心使命，落户深圳南山科技园',
    color: '#E11D48',
    glowColor: '#FFF1F2'
  }
]

export const coreAdvantages = [
  {
    icon: 'lucide:code',
    title: '技术实力雄厚',
    desc: '自研100+款SaaS产品，拥有完全自主知识产权。技术团队占比超70%，核心成员来自一线互联网企业，持续投入前沿技术研发',
    gradient: 'linear-gradient(135deg,#0066FF,#0044CC)',
    shadowColor: 'rgba(0,102,255,.2)'
  },
  {
    icon: 'lucide:shield',
    title: '安全稳定可靠',
    desc: '99.9%系统可用性SLA保障，数据多重备份、异地容灾。通过ISO27001信息安全认证，银行级数据加密保护企业核心资产',
    gradient: 'linear-gradient(135deg,#00B96B,#0891B2)',
    shadowColor: 'rgba(16,185,129,.2)'
  },
  {
    icon: 'lucide:smile',
    title: '服务体验卓越',
    desc: '7×24小时全天候技术支持，专属客户成功经理全程陪伴。3分钟快速上线，插件化配置无需开发，真正做到即买即用',
    gradient: 'linear-gradient(135deg,#7C3AED,#A78BFA)',
    shadowColor: 'rgba(124,58,237,.2)'
  },
  {
    icon: 'lucide:zap',
    title: '持续创新迭代',
    desc: '保持每月产品迭代节奏，紧跟AI大模型、大数据等前沿技术。500+企业客户的真实反馈驱动产品持续进化，永不过时',
    gradient: 'linear-gradient(135deg,#FF9500,#E11D48)',
    shadowColor: 'rgba(245,158,11,.2)'
  }
]

export const coreBusiness = [
  {
    icon: 'lucide:users',
    title: '企业陪跑孵化',
    desc: '从战略规划到落地执行，全程陪伴式服务。帮助企业快速构建数字化能力，降低试错成本，实现业务增长',
    gradient: 'linear-gradient(135deg,#0066FF,#0044CC)',
    lightBg: '#EBF5FF',
    shadowColor: 'rgba(0,102,255,.25)'
  },
  {
    icon: 'lucide:monitor',
    title: 'IT数字化',
    desc: '提供电商平台搭建、应用中台、数据中台等全方位技术解决方案，助力企业实现业务流程数字化、智能化升级',
    gradient: 'linear-gradient(135deg,#00B96B,#0891B2)',
    lightBg: '#ECFDF5',
    shadowColor: 'rgba(16,185,129,.25)'
  },
  {
    icon: 'lucide:trending-up',
    title: '企业运营营销',
    desc: '涵盖电商代运营、视觉设计、营销策划、电商培训指导、电商资源整合等多元化运营服务，提升企业线上竞争力',
    gradient: 'linear-gradient(135deg,#FF9500,#E11D48)',
    lightBg: '#FFFBEB',
    shadowColor: 'rgba(245,158,11,.25)'
  }
]

export const productSystem = [
  {
    icon: 'lucide:shopping-cart',
    title: '新零售电商',
    desc: '全渠道电商平台搭建，涵盖B2C/B2B/O2O等多种模式，打通线上线下，实现全场景数字化零售',
    bg: '#EBF5FF',
    color: '#0066FF'
  },
  {
    icon: 'lucide:package',
    title: '应用中台',
    desc: 'OA/CRM/ERP/MES/SRM等业务中台产品，统一企业应用架构，提升业务协同效率，降低系统建设成本',
    bg: '#ECFDF5',
    color: '#00B96B'
  },
  {
    icon: 'lucide:bar-chart-3',
    title: '数据中台',
    desc: '数据采集、治理、分析、可视化全链路解决方案，释放数据价值，驱动智能决策',
    bg: '#F3E8FF',
    color: '#7C3AED'
  }
]

export const philosophy = [
  {
    icon: 'lucide:eye',
    title: '想客户之所想',
    desc: '深入理解客户业务场景，以客户视角洞察需求，提供贴合实际的产品与服务方案'
  },
  {
    icon: 'lucide:search',
    title: '思客户之所需',
    desc: '持续迭代产品与服务体系，预判行业趋势，为客户提供前瞻性的数字化解决方案'
  },
  {
    icon: 'lucide:zap',
    title: '急客户之所急',
    desc: '快速响应客户需求，以高效的执行力和贴心的服务，帮助客户解决燃眉之急'
  }
]

export const customerLogos = [
  { name: '华为云', shortName: '华', gradient: 'linear-gradient(135deg,#0066FF,#0044CC)' },
  { name: '腾讯云', shortName: '腾', gradient: 'linear-gradient(135deg,#00B96B,#0891B2)' },
  { name: '阿里云', shortName: '阿', gradient: 'linear-gradient(135deg,#FF9500,#E11D48)' },
  { name: '京东云', shortName: '京', gradient: 'linear-gradient(135deg,#7C3AED,#A78BFA)' },
  { name: '字节跳动', shortName: '字', gradient: 'linear-gradient(135deg,#0891B2,#0066FF)' },
  { name: '美团', shortName: '美', gradient: 'linear-gradient(135deg,#E11D48,#FF9500)' }
]

// ===================== 招聘数据 =====================

export const jobBenefits = [
  { icon: 'lucide:shield', text: '六险一金', color: '#0066FF', bgColor: '#EBF5FF' },
  { icon: 'lucide:clock', text: '弹性工作', color: '#00B96B', bgColor: '#ECFDF5' },
  { icon: 'lucide:heart-pulse', text: '年度体检', color: '#FF9500', bgColor: '#FFFBEB' },
  { icon: 'lucide:users', text: '团建活动', color: '#7C3AED', bgColor: '#F3E8FF' },
  { icon: 'lucide:book-open', text: '学习成长', color: '#0891B2', bgColor: '#ECFEFF' },
  { icon: 'lucide:trending-up', text: '股权激励', color: '#E11D48', bgColor: '#FFF1F2' }
]

export const jobs: JobItem[] = [
  {
    id: 'job1',
    title: '高级前端工程师',
    department: '技术部',
    deptColor: '#0066FF',
    location: '上海',
    experience: '5-7年',
    education: '本科',
    salary: '25-40K',
    desc: '负责紫金山AI企业级数字化平台前端架构设计与核心模块开发，参与组件库建设、性能优化及工程化实践。',
    requirements: [
      '精通Vue3/TypeScript，5年以上前端开发经验',
      '熟悉Vite/Webpack等构建工具',
      '有大型SaaS平台开发经验优先',
      '了解微前端/组件库设计',
      '具备良好的代码风格和文档习惯'
    ]
  },
  {
    id: 'job2',
    title: '产品经理',
    department: '产品部',
    deptColor: '#00B96B',
    location: '上海',
    experience: '3-5年',
    education: '本科',
    salary: '20-35K',
    desc: '负责紫金山AI SaaS产品线的产品规划、需求分析和项目推进，协调研发、设计、运营团队完成产品迭代。',
    requirements: [
      '3年以上SaaS/B2B产品经验',
      '熟练使用Axure/Figma等原型工具',
      '具备数据分析能力',
      '有电商/CRM/ERP产品经验优先',
      '优秀的沟通协调和项目管理能力'
    ]
  },
  {
    id: 'job3',
    title: '后端架构师',
    department: '技术部',
    deptColor: '#0066FF',
    location: '上海',
    experience: '7年以上',
    education: '本科',
    salary: '35-60K',
    desc: '负责紫金山AI微服务架构设计与演进，核心业务系统开发，技术团队管理与技术规范制定。',
    requirements: [
      '7年以上Java后端开发经验',
      '精通Spring Cloud微服务架构',
      '熟悉MySQL/Redis/RabbitMQ',
      '有高并发系统设计经验',
      '具备技术团队管理经验'
    ]
  },
  {
    id: 'job4',
    title: '销售总监',
    department: '销售部',
    deptColor: '#FF9500',
    location: '上海/北京',
    experience: '5年以上',
    education: '本科',
    salary: '30-50K',
    desc: '负责紫金山AI SaaS产品线的大客户销售与渠道建设，带领销售团队完成业绩目标。',
    requirements: [
      '5年以上SaaS/软件行业销售经验',
      '有丰富的大客户资源',
      '具备团队管理经验',
      '出色的商务谈判和客户关系维护能力',
      '结果导向，抗压能力强'
    ]
  }
]

// ===================== 新闻数据 =====================

export const newsItems: NewsItem[] = [
  {
    id: 'n1',
    date: '2026-07-01',
    day: '01',
    month: '07月',
    title: '紫金山AI获C轮融资5亿元，加速企业级数字化生态布局',
    summary:
      '紫金山AI宣布完成C轮5亿元融资，由红杉资本领投。资金将用于产品研发迭代和生态建设，计划未来三年覆盖100万家企业用户。',
    category: 'company',
    content:
      '紫金山AI宣布完成C轮5亿元融资，由红杉资本领投。资金将用于产品研发迭代和生态建设，计划未来三年覆盖100万家企业用户。\n\n本轮融资将重点投入三个方面：一是加大AI大模型技术在SaaS产品中的应用，推出AI智能助手；二是扩展业务中台产品线，新增WMS仓储管理和SCM供应链管理；三是建设全国性代理商网络，覆盖200+城市。\n\n紫金山AI创始人表示："这轮融资标志着公司进入新阶段，我们将以更强的技术实力和更完善的服务体系，助力更多中小企业实现数字化转型。"'
  },
  {
    id: 'n2',
    date: '2026-06-25',
    day: '25',
    month: '06月',
    title: '商城系统V3.0重磅发布：支持多商户和跨境支付',
    summary:
      '商城系统V3.0正式发布，新增多商户管理、跨境支付、智能推荐三大核心功能，助力企业构建全渠道电商平台。',
    category: 'product',
    content:
      '商城系统V3.0正式发布，新增多商户管理、跨境支付、智能推荐三大核心功能。\n\n多商户管理：支持B2B2C模式，平台可入驻多个商户，独立店铺、独立结算、统一管理。\n\n跨境支付：集成PayPal、Stripe等国际支付方式，支持多币种结算，海外用户无障碍购物。\n\n智能推荐：基于用户行为分析，AI算法推荐相关商品，提升客单价和复购率。\n\nV3.0同时优化了性能，单实例支持10万QPS，分布式部署可弹性扩容。'
  },
  {
    id: 'n3',
    date: '2026-06-20',
    day: '20',
    month: '06月',
    title: '2026中国数字化行业报告：企业数字化率突破60%',
    summary:
      '据最新行业报告显示，2026年中国企业数字化率首次突破60%，数字化市场规模达到2000亿元，其中中小企业占比持续提升。',
    category: 'industry',
    content:
      '据最新行业报告显示，2026年中国企业数字化率首次突破60%，数字化市场规模达到2000亿元。\n\n报告指出，中小企业数字化率从2024年的35%提升至2026年的52%，增速显著。SaaS模式成为中小企业数字化的首选，占比超过70%。\n\n紫金山AI作为企业级数字化平台代表，服务500+企业客户，覆盖零售、制造、教育、医疗等多个行业，在中小企业SaaS市场份额持续领先。'
  }
]

// ===================== 活动数据 =====================

export const events: EventItem[] = [
  {
    id: 'evt1',
    title: '2026 数字化产业创新大会',
    desc: '探讨AI驱动数字化产业发展新趋势，汇聚行业领袖分享前沿洞察…',
    date: '2026-08-15',
    location: '上海',
    status: '即将开始',
    statusColor: '#0066FF',
    type: '线下峰会',
    gradient: 'linear-gradient(135deg,#0066FF,#7C3AED)',
    icon: 'lucide:users',
    image: 'https://picsum.photos/seed/event1/800/320'
  },
  {
    id: 'evt2',
    title: '紫金山AI产品使用培训',
    desc: '手把手教你搭建企业数字化平台，从建站到商城到数据中台全覆盖…',
    date: '2026-07-10',
    location: '线上',
    status: '进行中',
    statusColor: '#00B96B',
    type: '线上直播',
    gradient: 'linear-gradient(135deg,#00B96B,#0066FF)',
    icon: 'lucide:monitor',
    image: 'https://picsum.photos/seed/event2/800/320'
  },
  {
    id: 'evt3',
    title: '中小企业数字化转型实践沙龙',
    desc: '多位企业CTO分享数字化转型实战经验，圆桌讨论+案例拆解…',
    date: '2026-07-20',
    location: '深圳',
    status: '即将开始',
    statusColor: '#FF9500',
    type: '线下沙龙',
    gradient: 'linear-gradient(135deg,#FF9500,#E11D48)',
    icon: 'lucide:trophy',
    image: 'https://picsum.photos/seed/event3/800/320'
  }
]

// ===================== 帮助中心数据 =====================

export const helpCategories = [
  {
    id: 'quickstart',
    name: '快速入门',
    icon: 'lucide:rocket',
    color: '#0066FF',
    bgColor: '#EBF5FF',
    desc: '3分钟了解平台使用'
  },
  {
    id: 'faq',
    name: '常见问题',
    icon: 'lucide:help-circle',
    color: '#00B96B',
    bgColor: '#ECFDF5',
    desc: 'FAQ快速解答'
  },
  {
    id: 'video',
    name: '视频教程',
    icon: 'lucide:play-circle',
    color: '#7C3AED',
    bgColor: '#F3E8FF',
    desc: '手把手操作演示'
  },
  {
    id: 'api',
    name: 'API文档',
    icon: 'lucide:code',
    color: '#FF9500',
    bgColor: '#FFFBEB',
    desc: '开发者接口参考'
  }
]

export const helpQuickstart = [
  {
    step: 1,
    title: '注册并完成企业认证',
    desc: '注册账号后，上传营业执照完成企业认证，解锁全部购买功能'
  },
  {
    step: 2,
    title: '选择合适的产品',
    desc: '浏览产品中心，根据业务需求选择产品组合，支持多产品一起购买'
  },
  {
    step: 3,
    title: '一键部署上线',
    desc: '购买后系统自动部署，生成管理后台地址，最快3分钟即可上线'
  }
]

export const faqItems = [
  {
    id: 'faq1',
    question: '如何完成企业认证？',
    answer:
      '登录后进入个人中心→企业认证，填写企业信息并上传营业执照，提交后1-3个工作日内审核完成。审核通过后即可享受企业专享价格。'
  },
  {
    id: 'faq2',
    question: '购买后多久可以部署完成？',
    answer:
      '标准产品购买后3-5分钟即可完成自动部署，系统会生成独立的管理后台地址。定制化方案需额外1-2个工作日。'
  },
  {
    id: 'faq3',
    question: '支持退款吗？',
    answer: '购买后7天内支持无条件退款。超过7天按已使用时长折算退款，详情参考服务协议。'
  },
  {
    id: 'faq4',
    question: '数据安全保障如何？',
    answer:
      '采用银行级AES-256加密，数据三副本备份，通过ISO27001和等保三级认证。所有数据隔离存储，保障企业数据安全。'
  },
  {
    id: 'faq5',
    question: '多个产品可以组合购买吗？有没有优惠？',
    answer:
      '支持多个产品组合购买，购买3款及以上享9折优惠，5款及以上享8.5折。企业认证用户叠加额外折扣。欢迎联系销售团队获取专属组合报价。'
  },
  {
    id: 'faq6',
    question: '如何将已有网站迁移到紫金山AI？',
    answer:
      '我们提供免费的数据迁移服务。技术支持团队会协助您完成域名解析、数据导入、SEO重定向等全流程，确保平滑迁移，不影响现有业务。'
  },
  {
    id: 'faq7',
    question: '外贸官网支持哪些语言和支付方式？',
    answer:
      '外贸官网支持8种语言自动切换（中、英、日、韩、法、德、西、阿），集成PayPal、Stripe、国际信用卡等主流支付方式，海外CDN加速覆盖全球200+节点。'
  },
  {
    id: 'faq8',
    question: '支持二次开发和API对接吗？',
    answer:
      '完全支持。我们提供完善的RESTful API和Webhook，支持与ERP、CRM、第三方支付等系统对接。开放平台还支持ISV伙伴开发应用上架市场。'
  }
]

export const helpVideos = [
  {
    title: '平台快速入门教程',
    desc: '从注册到购买，完整流程演示',
    duration: '08:32',
    gradient: 'linear-gradient(135deg,#001B5E,#0044CC)'
  },
  {
    title: '官网建设操作指南',
    desc: '可视化建站全流程详解',
    duration: '12:15',
    gradient: 'linear-gradient(135deg,#7C3AED,#A78BFA)'
  },
  {
    title: '数据中台配置教程',
    desc: '数据接入与可视化看板搭建',
    duration: '15:42',
    gradient: 'linear-gradient(135deg,#00B96B,#0891B2)'
  },
  {
    title: '会员体系配置教程',
    desc: '等级积分与权益管理',
    duration: '09:18',
    gradient: 'linear-gradient(135deg,#FF9500,#E11D48)'
  }
]

export const helpApis = [
  {
    method: 'GET',
    endpoint: '/api/v1/products',
    desc: '获取产品列表，支持分类筛选和分页',
    example:
      'curl -X GET "https://api.cloudsaas.cn/v1/products" \\\n  -H "Authorization: Bearer YOUR_API_KEY"'
  },
  {
    method: 'POST',
    endpoint: '/api/v1/orders',
    desc: '创建订单，需企业认证后调用',
    example:
      'curl -X POST "https://api.cloudsaas.cn/v1/orders" \\\n  -H "Authorization: Bearer YOUR_API_KEY" \\\n  -d \'{"product_id":"website","duration":1}\''
  },
  {
    method: 'DELETE',
    endpoint: '/api/v1/orders/{id}',
    desc: '取消订单，需在7天内操作',
    example:
      'curl -X DELETE "https://api.cloudsaas.cn/v1/orders/ORD123" \\\n  -H "Authorization: Bearer YOUR_API_KEY"'
  }
]

export const helpDocCategories = [
  {
    name: '官网建设',
    docCount: '12篇文档',
    icon: 'lucide:globe',
    color: '#0066FF',
    bgColor: '#EBF5FF',
    productId: 'website'
  },
  {
    name: '商城系统',
    docCount: '18篇文档',
    icon: 'lucide:shopping-cart',
    color: '#E11D48',
    bgColor: '#FFF1F2',
    productId: 'shop'
  },
  {
    name: '会员系统',
    docCount: '8篇文档',
    icon: 'lucide:credit-card',
    color: '#FF9500',
    bgColor: '#FFFBEB',
    productId: 'member'
  },
  {
    name: '业务中台系列',
    docCount: '25篇文档',
    icon: 'lucide:briefcase',
    color: '#0891B2',
    bgColor: '#ECFEFF',
    productId: 'biz'
  },
  {
    name: '数据中台',
    docCount: '15篇文档',
    icon: 'lucide:database',
    color: '#7C3AED',
    bgColor: '#F3E8FF',
    productId: 'data'
  },
  {
    name: 'AI智能系列',
    docCount: '10篇文档',
    icon: 'lucide:users',
    color: '#00B96B',
    bgColor: '#ECFDF5',
    productId: 'ai-social'
  }
]

// ===================== 免费试用产品列表 =====================

export const trialProducts = [
  {
    id: 'geo',
    name: 'GEO',
    icon: 'lucide:sparkles',
    color: '#7C3AED',
    bgColor: '#F3E8FF',
    desc: 'AI搜索优化，结构化标记'
  },
  {
    id: 'ai-shop',
    name: 'AI电商系统',
    icon: 'lucide:shopping-cart',
    color: '#E11D48',
    bgColor: '#FFF1F2',
    desc: 'AI驱动多端商城'
  },
  {
    id: 'website',
    name: '品牌官网',
    icon: 'lucide:globe',
    color: '#0066FF',
    bgColor: '#EBF5FF',
    desc: '拖拽式建站，300+行业模板'
  },
  {
    id: 'ai-social',
    name: 'AI社交系统',
    icon: 'lucide:users',
    color: '#0066FF',
    bgColor: '#EBF5FF',
    desc: 'AI智能匹配，电子名片'
  },
  {
    id: 'ai-member',
    name: 'AI新零售会员系统',
    icon: 'lucide:credit-card',
    color: '#FF9500',
    bgColor: '#FFFBEB',
    desc: 'AI驱动会员运营体系'
  },
  {
    id: 'ai-course',
    name: 'AI培训系统',
    icon: 'lucide:graduation-cap',
    color: '#7C3AED',
    bgColor: '#F3E8FF',
    desc: 'AI赋能直播录播培训'
  }
]
